import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { colors, spacing, borderRadius, shadows, typography } from '../../../styles/design-system'

interface AdminFilterSidebarProps {
  isOpen: boolean
  title?: string
  children: React.ReactNode
  onClose: () => void
  onApply?: () => void
  onClear?: () => void
  applyLabel?: string
  clearLabel?: string
  isApplyDisabled?: boolean
  width?: number
}

const DEFAULT_WIDTH = 360

const AdminFilterSidebar: React.FC<AdminFilterSidebarProps> = ({
  isOpen,
  title = 'Filtros',
  children,
  onClose,
  onApply,
  onClear,
  applyLabel = 'Aplicar filtros',
  clearLabel = 'Limpar filtros',
  isApplyDisabled = false,
  width = DEFAULT_WIDTH
}) => {
  const [mounted, setMounted] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const firstInteractiveRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    if (isOpen) {
      document.body.style.overflow = 'hidden'
      window.setTimeout(() => {
        if (firstInteractiveRef.current) {
          firstInteractiveRef.current.focus()
        } else {
          sidebarRef.current?.focus()
        }
      }, 10)
    } else {
      document.body.style.overflow = ''
      previouslyFocused?.focus()
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen, mounted])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!mounted || !isOpen) {
    return null
  }

  const handleApply = () => {
    onApply?.()
  }

  const handleClear = () => {
    onClear?.()
  }

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-filter-sidebar-title"
      tabIndex={-1}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1100
      }}
    >
      <aside
        ref={sidebarRef}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: `${width}px`,
          height: '100%',
          backgroundColor: colors.neutral.white,
          boxShadow: shadows.xl,
          borderTopLeftRadius: borderRadius['2xl'],
          borderBottomLeftRadius: borderRadius['2xl'],
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <header
          style={{
            padding: spacing.lg,
            borderBottom: `1px solid ${colors.neutral.gray200}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.sm
          }}
        >
          <h2
            id="admin-filter-sidebar-title"
            style={{
              margin: 0,
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.semibold,
              color: colors.neutral.gray800
            }}
          >
            {title}
          </h2>
          <button
            ref={firstInteractiveRef}
            type="button"
            onClick={onClose}
            aria-label="Fechar filtros"
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.neutral.gray500,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: spacing.sm,
              borderRadius: borderRadius.md,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out'
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = colors.neutral.gray100
              event.currentTarget.style.color = colors.neutral.gray700
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = 'transparent'
              event.currentTarget.style.color = colors.neutral.gray500
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </header>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: spacing.lg,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.lg
          }}
        >
          {children}
        </div>

        <footer
          style={{
            padding: spacing.lg,
            borderTop: `1px solid ${colors.neutral.gray200}`,
            display: 'flex',
            gap: spacing.sm,
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}
        >
          <button
            type="button"
            onClick={handleClear}
            disabled={!onClear}
            style={{
              flex: 1,
              minWidth: '140px',
              borderRadius: borderRadius.lg,
              border: `1px solid ${colors.neutral.gray300}`,
              backgroundColor: colors.neutral.white,
              color: colors.neutral.gray700,
              fontWeight: typography.fontWeight.medium,
              padding: `${spacing.sm} ${spacing.md}`,
              cursor: onClear ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease-in-out',
              opacity: onClear ? 1 : 0.6
            }}
            onMouseEnter={(event) => {
              if (!onClear) return
              event.currentTarget.style.backgroundColor = colors.neutral.gray100
            }}
            onMouseLeave={(event) => {
              if (!onClear) return
              event.currentTarget.style.backgroundColor = colors.neutral.white
            }}
          >
            {clearLabel}
          </button>

          <button
            type="button"
            onClick={handleApply}
            disabled={isApplyDisabled}
            style={{
              flex: 1,
              minWidth: '160px',
              borderRadius: borderRadius.lg,
              border: 'none',
              backgroundColor: isApplyDisabled ? colors.neutral.gray300 : colors.primary.blue,
              color: colors.neutral.white,
              fontWeight: typography.fontWeight.semibold,
              padding: `${spacing.sm} ${spacing.md}`,
              cursor: isApplyDisabled ? 'not-allowed' : 'pointer',
              opacity: isApplyDisabled ? 0.7 : 1,
              transition: 'all 0.2s ease-in-out',
              boxShadow: shadows.sm,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(event) => {
              if (isApplyDisabled) return
              event.currentTarget.style.backgroundColor = colors.secondary.darkBlue
            }}
            onMouseLeave={(event) => {
              if (isApplyDisabled) return
              event.currentTarget.style.backgroundColor = colors.primary.blue
            }}
          >
            {applyLabel}
          </button>
        </footer>
      </aside>
    </div>,
    document.body
  )
}

export default AdminFilterSidebar

